/**
 * Collaborative Product Mapping (CPM) API Service
 *
 * This file contains TypeScript API functions for collaborative product mapping routes.
 * Migrated from supply-chains v2 endpoints.
 *
 * All functions use the "cpm" prefix to distinguish from legacy supply-chain functions.
 * Endpoints: /v3/supply-chains/* (mapped to /product-mapping/* for new style structure)
 *
 * IMPORTANT: Keep function signatures identical to v2 source.
 * No optimization or refactoring - direct ports only.
 */

import { fetch as fetchRaw } from '@lib/fetch';
import { getDefinedUrlSearchParams } from '@lib/util';
import { backend } from '@services/services';

const fetch = (url: string, ...args: any[]) =>
  fetchRaw([backend.url, url], ...args);

// ============================================================================
// Generic Response Types
// ============================================================================

// Unwrapped response type
export interface V2ApiResponse<T> {
  data: T | null;
  error: {
    message?: string;
    reqId?: string;
    // `expected: true` means a controlled, user-facing error (404, 403, validation, etc.) — not a platform failure.
    // Absent/false means a real error that should be reported to Datadog.
    expected?: boolean;
    statusCode?: number;
  } | null;
  status: number;
}

// Raw backend response shape (has metadata wrapper)
interface BackendResponse<T> {
  metadata?: { success: boolean };
  data?: T;
}

/**
 * Fetch helper that unwraps the { metadata, data } wrapper from the
 * new /cascade-tracing/ API endpoints, returning just the inner data.
 */
const v2Fetch = async <T>(
  url: string,
  options: RequestInit,
): Promise<V2ApiResponse<T>> => {
  const response = await fetchRaw([backend.url, url], options);

  if (response.error || !response.data) {
    return response as V2ApiResponse<T>;
  }

  const backendData = response.data as BackendResponse<T>;
  if (backendData && typeof backendData === 'object' && 'data' in backendData) {
    return {
      ...response,
      data: backendData.data ?? null,
    };
  }

  return response as V2ApiResponse<T>;
};

// ============================================================================
// CPM Request/Response Types
// ============================================================================

/**
 * CPM Mapping (Supply Chain) entity
 */
export interface ICpmMapping {
  id: string;
  name: string;
  companyId: string;
  company?: {
    id: string;
    name: string;
  };
  createdByCompanyId: string;
  collaborationStatus: string;
  collaborationType: string;
  updatedAt: string;
  updatedByUserName?: string;
  updatedByCompanyName?: string;
  isArchived?: boolean;
  status?: string;
  [key: string]: unknown;
}

/**
 * CPM Mappings list response
 */
export interface ICpmMappingsListResponse {
  data: ICpmMapping[];
  meta?: {
    page: number;
    count: number;
    total: number;
    pageSize: number;
  };
}

/**
 * CPM Mapping create request
 */
export interface ICpmMappingCreateBody {
  name: string;
  featuredFacilityProcessId: string;
}

/**
 * CPM Mapping update request
 */
export interface ICpmMappingUpdateBody {
  name?: string;
  [key: string]: unknown;
}

/**
 * CPM Node details response
 */
export interface ICpmNodeDetails {
  id: string;
  nodeId: string;
  [key: string]: unknown;
}

/**
 * CPM Link details response
 */
export interface ICpmLinkDetails {
  id: string;
  linkId: string;
  [key: string]: unknown;
}

/**
 * CPM Link update request
 */
export interface ICpmLinkUpdateBody {
  [key: string]: unknown;
}

/**
 * CPM Facility process update request
 */
export interface ICpmFacilityProcessUpdateBody {
  featuredFacilityProcessId?: string;
  [key: string]: unknown;
}

/**
 * CPM Extend request
 */
export interface ICpmExtendBody {
  [key: string]: unknown;
}

/**
 * CPM Copy request
 */
export interface ICpmCopyBody {
  id: string;
}

/**
 * CPM Bulk archive request
 */
export interface ICpmBulkArchiveBody {
  ids: string[];
}

/**
 * CPM Bulk import request
 */
export interface ICpmBulkImportBody {
  fileId: string;
}

export interface ICollaboratorData {
  id: string;
  companyId: string;
  collaborationType: string;
  collaborationStatus: string | null;
  message?: string | null;
  createdByCompanyId: string | null;
  invitedByCompanyId?: string | null;
  fromCollaboratorId?: string;
  company: { name?: string | null; officialName?: string | null };
  facilityProcess?: { id: string; name: string } | null;
  invitedByCompany: {
    id: string;
    name?: string | null;
    officialName: string;
  };
  invitedByUser?: { id?: string | null; name?: string | null } | null;
  createdAt: string;
}

export interface ICollaboratorDetail extends ICollaboratorData {
  createdByUserId: string | null;
  createdAt: string;
  supplyChain: {
    id: string;
    name?: string | null;
    status: string;
  };
}

// ============================================================================
// CPM API Functions - List & Fetch
// ============================================================================

/**
 * Get CPM mappings with pagination, filtering, sorting
 * Route: GET /product-mapping/list
 *
 * @param queryParams - Query parameters (page, sort, filter, count, isArchived)
 * @returns Promise with list of CPM mappings
 */
export const cpmMappingsList = (queryParams: {
  page?: number;
  sort?: string;
  order?: string;
  filter?: string;
  limit?: number;
  isArchived?: boolean;
}) => {
  return fetch('/product-mapping/list', {
    method: 'POST',
    body: JSON.stringify(queryParams),
  });
};

/**
 * Get collaborators downstream from a specific collaborator
 * Route: POST /product-mapping/collaborators/list
 *
 * @param queryParams - Query parameters with collaboratorId and optional filters
 * @returns Promise with list of downstream collaborators
 */
export const cpmMappingCollaboratorsList = (queryParams: {
  collaboratorId: string;
  collaborationStatus?: string;
  collaborationType?: string;
  page?: number;
  limit?: number;
}): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch('/product-mapping/collaborators/list', {
    method: 'POST',
    body: JSON.stringify(queryParams),
  });
};

/**
 * Get single CPM mapping details
 * Route: GET /v3/supply-chains/{id}
 *
 * @param id - CPM mapping ID
 * @param queryParams - Optional query parameters
 * @returns Promise with CPM mapping data
 */
export const cpmMappingGet = (
  id: string,
  queryParams?: unknown,
): Promise<V2ApiResponse<ICpmMapping>> => {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return v2Fetch(`/v3/supply-chains/${id}${params}`, {
    method: 'GET',
  });
};

/**
 * Get CPM collaborator graph
 * Route: GET /product-mapping/collaborators/{collaboratorId}/graph
 *
 * @param collaboratorId - Collaborator ID
 * @returns Promise with CPM mapping data
 */
export const cpmMappingGetGraph = (
  collaboratorId: string,
): Promise<V2ApiResponse<ICpmMapping>> => {
  return v2Fetch(`/product-mapping/collaborators/${collaboratorId}/graph`, {
    method: 'GET',
  });
};

/**
 * Attach an existing product map to a collaborator's product map
 * Route: POST /product-mapping/collaborators/{id}/attach-existing
 *
 * @param collaboratorId - Collaborator ID
 * @param body - Payload with productMapIdToAttach and tracingNodeIdToAttachFrom
 * @returns Promise with updated product map data
 */
export const cpmAttachProductMap = (
  collaboratorId: string,
  body: { productMapIdToAttach: string; tracingNodeIdToAttachFrom: string },
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch(
    `/product-mapping/collaborators/${collaboratorId}/attach-existing`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
};

/**
 * Get CPM node details
 * Route: GET /v3/supply-chains/asset
 *
 * @param queryParams - Query parameters
 * @returns Promise with node details
 */
export const cpmNodeDetailsGet = (
  queryParams: unknown,
): Promise<V2ApiResponse<ICpmNodeDetails>> => {
  return v2Fetch(
    `/v3/supply-chains/asset?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
};

/**
 * Get CPM link details
 * Route: GET /v3/supply-chains/link/{id}
 *
 * @param id - Link ID
 * @param queryParams - Query parameters
 * @returns Promise with link details
 */
export const cpmLinkDetailsGet = (
  id: string,
  queryParams: unknown,
): Promise<V2ApiResponse<ICpmLinkDetails>> => {
  return v2Fetch(
    `/v3/supply-chains/link/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
};

// ============================================================================
// CPM API Functions - Create
// ============================================================================

/**
 * Create new CPM mapping
 * Route: POST /v3/supply-chains
 *
 * @param body - Create request payload
 * @returns Promise with created CPM mapping
 */
export const cpmMappingsCreate = (
  body: ICpmMappingCreateBody,
): Promise<V2ApiResponse<ICpmMapping>> => {
  return v2Fetch('/product-mapping', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Copy existing CPM mapping
 * Route: POST /v3/supply-chains/copy
 *
 * @param body - Copy request payload (contains id of source mapping)
 * @returns Promise with cloned CPM mapping
 */
export const copyCpmMapping = (
  body: ICpmCopyBody,
): Promise<V2ApiResponse<ICpmMapping>> => {
  return v2Fetch('/v3/supply-chains/copy', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Extend CPM mapping with new nodes/links (Collaborator flow)
 * Route: POST /product-mapping/collaborators/{id}/node-extend
 *
 * @param collaboratorId - Collaborator ID (not supply chain ID)
 * @param body - Extend request payload
 * @returns Promise with updated mapping
 */
export const cpmExtend = (
  collaboratorId: string,
  body: ICpmExtendBody,
): Promise<V2ApiResponse<ICpmMapping>> => {
  return v2Fetch(
    `/product-mapping/collaborators/${collaboratorId}/node-extend`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
};

/**
 * Bulk import CPM mappings from file
 * Route: POST /product-mapping/bulk-import
 *
 * @param body - Import request with fileId
 * @returns Promise with import result
 */
export const cpmBulkImport = (
  body: ICpmBulkImportBody,
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch('/product-mapping/bulk-import', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

// ============================================================================
// CPM API Functions - Update
// ============================================================================

/**
 * Update CPM mapping (e.g., name)
 * Route: PATCH /v3/supply-chains/{id}
 *
 * @param id - CPM mapping ID
 * @param body - Update request payload
 * @returns Promise with updated mapping
 */
export const cpmMappingUpdate = (
  id: string,
  body: ICpmMappingUpdateBody,
): Promise<V2ApiResponse<ICpmMapping>> => {
  return v2Fetch(`/v3/supply-chains/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

/**
 * Update CPM supply chain mapping status
 * Route: PATCH /product-mapping/{id}/status
 *
 * @param id - Supply chain mapping ID
 * @param status - New status: IN_PROGRESS | IN_REVIEW | COMPLETED
 * @returns Promise with void response (204 no content)
 */
export const updateSupplyChainMappingStatus = (
  id: string,
  status: 'IN_PROGRESS' | 'IN_REVIEW' | 'COMPLETED',
): Promise<V2ApiResponse<null>> => {
  return v2Fetch(`/product-mapping/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
};

/**
 * Update CPM link details
 * Route: PATCH /v3/supply-chains/links/{id}
 *
 * @param id - Link ID
 * @param body - Update request payload
 * @returns Promise with updated link
 */
export const cpmLinkDetailUpdate = (
  id: string,
  body: ICpmLinkUpdateBody,
): Promise<V2ApiResponse<ICpmLinkDetails>> => {
  return v2Fetch(`/v3/supply-chains/links/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

/**
 * Update CPM node facility process
 * Route: PATCH /v3/supply-chains/nodes/{nodeId}/featured-facility-process
 *
 * @param nodeId - Node ID
 * @param body - Update request payload
 * @returns Promise with update result
 */
export const cpmFacilityProcessUpdate = (
  nodeId: string,
  body: ICpmFacilityProcessUpdateBody,
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch(
    `/v3/supply-chains/nodes/${nodeId}/featured-facility-process`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
};

// ============================================================================
// CPM API Functions - Delete
// ============================================================================

/**
 * Delete CPM node
 * Route: DELETE /v3/supply-chains/nodes/{id}
 *
 * @param id - Node ID
 * @param queryParams - Optional query parameters
 * @returns Promise with delete result
 */
export const cpmMappingNodeDelete = (
  collaboratorId: string,
  nodeId: string,
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch(
    `/product-mapping/collaborators/${collaboratorId}/node/${nodeId}`,
    {
      method: 'DELETE',
    },
  );
};

// ============================================================================
// CPM API Functions - Archive
// ============================================================================

/**
 * Bulk archive CPM mappings
 * Route: POST /v3/supply-chains/archive
 *
 * @param body - Archive request with IDs to archive
 * @returns Promise with archive result
 */
export const cpmBulkArchive = (
  body: ICpmBulkArchiveBody,
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch('/v3/supply-chains/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Bulk unarchive CPM mappings
 * Route: POST /product-mapping/unarchive
 *
 * @param body - Unarchive request with IDs to unarchive
 * @returns Promise with unarchive result
 */
export const cpmBulkUnarchive = (
  body: ICpmBulkArchiveBody,
): Promise<V2ApiResponse<{ successIds: string[]; notFoundIds: string[] }>> => {
  return v2Fetch('/product-mapping/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Create a new collaborator invitation
 * Route: POST /product-mapping/{supplyChainId}/collaborators
 *
 * @param supplyChainId - Supply chain ID
 * @param body - Collaborator creation payload (companyId, tracingNodeId, message)
 * @returns Promise with created collaborator result
 */
export const createCollaborator = (
  supplyChainId: string,
  body: {
    companyId: string;
    tracingNodeId?: string;
    fromCollaboratorId: string;
    message?: string;
  },
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch(`/product-mapping/${supplyChainId}/collaborators`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Update collaborator status (approve or request changes)
 * Route: PATCH /product-mapping/collaborators/{collaboratorId}/status
 *
 * @param collaboratorId - Collaborator ID
 * @param body - Status update payload (status and optional message)
 * @returns Promise with update result
 */
export const updateCollaboratorStatus = (
  collaboratorId: string,
  body: {
    status: 'APPROVED' | 'REQUEST_CHANGES' | 'ACCEPTED' | 'IN_REVIEW';
    message?: string;
  },
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch(`/product-mapping/collaborators/${collaboratorId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const sendCollaboratorReminder = (
  collaboratorId: string,
  body: {
    status: string;
    shouldSendEmail?: boolean;
    shouldSendInApp?: boolean;
  },
): Promise<V2ApiResponse<unknown>> => {
  return v2Fetch(`/product-mapping/collaborators/${collaboratorId}/remind`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Get collaborator detail
 * Route: GET /product-mapping/collaborators/{collaboratorId}/detail
 *
 * @param collaboratorId - Collaborator ID
 * @returns Promise with collaborator detail
 */
export const getCollaboratorDetail = (
  collaboratorId: string,
): Promise<V2ApiResponse<ICollaboratorDetail>> => {
  return v2Fetch(`/product-mapping/collaborators/${collaboratorId}`, {
    method: 'GET',
  });
};

/**
 * Enriched communication item with user and company info
 */
export interface IEnrichedCommunicationItem {
  id: string;
  collaboratorId: string;
  tracingNodeId?: string | null;
  message?: string | null;
  action: string;
  createdAt?: string | null;
  createdByUserId?: string | null;
  createdByCompanyId?: string | null;
  userName?: string | null;
  companyName?: string | null;
}

/**
 * Paginated communications response
 */
export interface IPaginatedCommunicationsResponse {
  metadata?: {
    success: boolean;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  data: IEnrichedCommunicationItem[];
}

/**
 * List collaborator communications with pagination and enrichment
 * Route: POST /product-mapping/collaborators/{collaboratorId}/communication/list
 *
 * @param collaboratorId - Collaborator ID (not supply chain ID)
 * @param params - Optional pagination parameters (page, limit, createdForMe)
 * @returns Promise with paginated enriched communications
 */
export const listCollaboratorCommunicationsList = (
  collaboratorId: string,
  params?: { page?: number; limit?: number; createdForMe?: boolean },
): Promise<V2ApiResponse<IPaginatedCommunicationsResponse>> => {
  return v2Fetch(
    `/product-mapping/collaborators/${collaboratorId}/communication/list`,
    {
      method: 'POST',
      body: JSON.stringify({
        page: params?.page ?? 1,
        limit: params?.limit ?? 3,
        createdForMe: params?.createdForMe ?? false,
      }),
    },
  );
};
export interface BulkCreateProductMapsBody {
  styleIds: string[];
  supplier: { companyId: string; facilityProcessId: string };
  invitationMessage?: string;
  duplicateStrategy?: 'skip' | 'duplicate';
}

export interface BulkCreateProductMapsResponse {
  created: number;
  supplyChainIds: string[];
  skippedStyleIds?: string[];
}

export interface BulkCreateDuplicateCheckResponse {
  duplicatedStyleIds: string[];
}

export const cpmBulkCreate = (
  body: BulkCreateProductMapsBody,
): Promise<
  V2ApiResponse<
    BulkCreateProductMapsResponse | BulkCreateDuplicateCheckResponse
  >
> => {
  return v2Fetch('/product-mapping/bulk-create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export interface BulkExportResponse {
  exportId: string;
  message: string;
}

export const cpmBulkExport = (
  collaboratorIds?: string[],
): Promise<V2ApiResponse<BulkExportResponse>> => {
  return v2Fetch('/product-mapping/bulk-export', {
    method: 'POST',
    body: JSON.stringify({ collaboratorIds: collaboratorIds ?? [] }),
  });
};

/**
 * Copy a product mapping (supply chain) without collaborators
 * Route: POST /product-mapping/collaborators/{id}/copy
 *
 * @param collaboratorId - Collaborator ID
 * @param body - Copy request with optional name for the new mapping
 * @returns Promise with the new supply chain mapping
 */
export const copyProductMapping = (
  collaboratorId: string,
  body?: { name?: string },
): Promise<V2ApiResponse<ICpmMapping>> => {
  return v2Fetch(`/product-mapping/collaborators/${collaboratorId}/copy`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
};

/**
 * Delete a collaborator
 * Route: DELETE /product-mapping/collaborators/{id}
 *
 * @param collaboratorId - Collaborator ID
 * @returns Promise with void response (204 no content)
 */
export const deleteCollaborator = (
  collaboratorId: string,
): Promise<V2ApiResponse<null>> => {
  return v2Fetch(`/product-mapping/collaborators/${collaboratorId}`, {
    method: 'DELETE',
  });
};
